import {GameBoard, GameConfig, GameData, GamePlay, GamePlayKey, GameSetup} from "../types";
import {v4 as uuid} from "uuid";

export class SlidleLayout {

  constructor(readonly level: SlidleDifficultyLevel, readonly original: HTMLCanvasElement, readonly tiles: HTMLCanvasElement[]) {
  }
}

export class SlidleDifficultyLevel {

  static readonly VALUES = new Array<SlidleDifficultyLevel>(4);
  static readonly EASY = SlidleDifficultyLevel.VALUES[0] = new SlidleDifficultyLevel("easy", "Easy", "Easy desc", 3);
  static readonly MEDIUM = SlidleDifficultyLevel.VALUES[1] = new SlidleDifficultyLevel("medium", "Medium", "Medium desc", 4);
  static readonly HARD = SlidleDifficultyLevel.VALUES[2] = new SlidleDifficultyLevel("hard", "Hard", "Hard desc", 5);
  static readonly VERY_HARD = SlidleDifficultyLevel.VALUES[3] = new SlidleDifficultyLevel("very_hard", "Very Hard", "Hard desc", 6);

  constructor(readonly name: string, readonly text: string, readonly description: string, readonly tilesCount: number) {
  }
}

export class SlidleGameConfig extends GameConfig {

  constructor(readonly level: SlidleDifficultyLevel) {
    super();
  }
}

export class SlidleGameSetup extends GameSetup {

  constructor(key: GamePlayKey) {
    super(key);
  }
}

export function findEmptyIndex(tilesCount: number, tileIndices: number[]): number {
  const totalTiles = tilesCount * tilesCount;
  return tileIndices.findIndex(tileIndex => tileIndex === totalTiles - 1);
}

export function validMoveIndex(tilesCount: number, tileIndices: number[], move: "Up" | "Down" | "Left" | "Right" | string): number {
  const totalTiles = tilesCount * tilesCount;
  const emptyIndex = findEmptyIndex(tilesCount, tileIndices);
  let n: number;
  switch (move) {
    case "Up":
      return (n = Math.floor(emptyIndex + tilesCount)) < totalTiles ? n : -1;
    case "Down":
      return (n = Math.floor(emptyIndex - tilesCount)) >= 0 ? n : -1;
    case "Left":
      return Math.floor((n = (emptyIndex + 1)) / tilesCount) === Math.floor(emptyIndex / tilesCount) ? n : -1;
    case "Right":
      return Math.floor((n = (emptyIndex - 1)) / tilesCount) === Math.floor(emptyIndex / tilesCount) ? n : -1;
  }
  return -1;
}

export function findSwapIndices(tilesCount: number, tileIndices: number[]): number[] {
  const swapIndices: number[] = [];
  let n: number;
  for (const move of ["Up", "Down", "Left", "Right"]) {
    if ((n = validMoveIndex(tilesCount, tileIndices, move)) >= 0) {
      swapIndices.push(n);
    }
  }
  return swapIndices;
}

export class SlidleGameData extends GameData<SlidleGameBoard> {

  static async create(config: SlidleGameConfig): Promise<SlidleGameData> {
    const loader = new SlidleGamesLoader();
    const layout = await loader.getRandomGame(config.level);
    const setup = new SlidleGameSetup(new GamePlayKey("slidle", uuid()));
    const tileIndices: number[] = [];
    const tilesCount = config.level.tilesCount;
    const totalTiles = tilesCount * tilesCount;
    for (let i = 0; i < totalTiles; i++) {
      tileIndices.push(i);
    }
    for (let i = 0; i < 200; i++) {
      const emptyIndex = findEmptyIndex(tilesCount, tileIndices);
      const swapIndices = findSwapIndices(tilesCount, tileIndices);
      // console.log(validSwap.swapIndices);
      const swapIndex = swapIndices[Math.floor(Math.random() * swapIndices.length)];
      let t = tileIndices[swapIndex];
      tileIndices[swapIndex] = tileIndices[emptyIndex];
      tileIndices[emptyIndex] = t;
    }
    const now = Date.now();
    return new SlidleGameData(new GamePlay(setup, now, 0, 0, 0, new SlidleGameBoard(config.level, layout, tileIndices)));
  }
}

export class SlidleGamesLoader {

  async getRandomGame(level: SlidleDifficultyLevel): Promise<SlidleLayout> {
    const tilesCount = level.tilesCount;
    const imagesCount = 58;
    const imageSize = 720;
    const tileSize = imageSize / tilesCount;
    const imageIndex = Math.floor(Math.random() * imagesCount);
    const image = await (await fetch(`/games/slidle/images/${imageIndex + 1}.png`)).blob();
    const imageBitmap = await createImageBitmap(image);
    const tiles: HTMLCanvasElement[] = [];
    for (let i = 0; i < tilesCount; i++) {
      for (let j = 0; j < tilesCount; j++) {
        const canvas = Object.assign(document.createElement("canvas"), {width: tileSize, height: tileSize});
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imageBitmap, -j * tileSize, -i * tileSize);
        tiles.push(canvas);
      }
    }
    const original = Object.assign(document.createElement("canvas"), {width: imageSize, height: imageSize});
    const ctx = original.getContext('bitmaprenderer');
    ctx.transferFromImageBitmap(imageBitmap);
    return new SlidleLayout(level, original, tiles);
  }
}

export class SlidleGameBoard extends GameBoard {

  constructor(readonly level: SlidleDifficultyLevel, readonly layout: SlidleLayout, readonly tileIndices: number[]) {
    super();
  }

  getPoints(moves: number): number {
    return Math.max(0, (25 - moves) * 50);
  }

  toJSON(): any {
    return this;
  }
}